import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { tajMahalCanvas } from "@/lib/tajMahalTexture";

// A vanity-sized top (cm) so the edge stays big in frame. Each run reaches the centre
// line, so four mitred runs tile the whole top and no cut ends ever show.
const TOP_L = 64;
const TOP_W = 44;
const DEPTH = TOP_W / 2;
const SLAB = 3;
const CAB_H = 42;
const TARGET = [TOP_L / 2, -1, -TOP_W / 2];

// front (z=0), right (x=L), back (z=-W), left (x=0): 3D position of a profile point at
// depth d / height h, s along the run; outward normal from the 2D one; and where the
// unrolled texture coordinate lands so the pattern flows from the top over the edge.
const RUNS = [
  { L: TOP_L, pos: (d, h, s) => [s, h, -d], nrm: (nd, nh) => [0, nh, -nd], uv: (u, s) => [s, -u] },
  { L: TOP_W, pos: (d, h, s) => [TOP_L - d, h, -s], nrm: (nd, nh) => [-nd, nh, 0], uv: (u, s) => [TOP_L - u, -s] },
  { L: TOP_L, pos: (d, h, s) => [TOP_L - s, h, -TOP_W + d], nrm: (nd, nh) => [0, nh, nd], uv: (u, s) => [TOP_L - s, -TOP_W + u] },
  { L: TOP_W, pos: (d, h, s) => [d, h, -TOP_W + s], nrm: (nd, nh) => [nd, nh, 0], uv: (u, s) => [u, -TOP_W + s] },
];

// SVG profile (10 units = 1 cm, top at y=40, face at x=150) → points with depth from the
// face, height below the top, and the unrolled coordinate the texture follows over the edge.
const outline = (profile) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg"><path d="${profile.path}"/></svg>`;
  const raw = new SVGLoader()
    .parse(svg)
    .paths[0].subPaths[0].getPoints(18)
    .map((p) => ({ d: p.x <= 0.01 ? DEPTH : (150 - p.x) / 10, h: -(p.y - 40) / 10 }));
  const pts = raw.filter(
    (p, i) => !i || Math.hypot(p.d - raw[i - 1].d, p.h - raw[i - 1].h) > 1e-4,
  );
  const last = pts[pts.length - 1];
  if (Math.hypot(last.d - pts[0].d, last.h - pts[0].h) > 1e-4) pts.push({ ...pts[0] });
  let onTop = true;
  pts.forEach((p, i) => {
    if (p.h < -1e-6) onTop = false;
    p.u = onTop
      ? p.d
      : pts[i - 1].u - Math.hypot(p.d - pts[i - 1].d, p.h - pts[i - 1].h);
  });
  return pts;
};

const buildTop = (profile) => {
  const pts = outline(profile);
  let area = 0;
  for (let i = 1; i < pts.length; i++) area += pts[i - 1].d * pts[i].h - pts[i].d * pts[i - 1].h;
  const sign = area > 0 ? 1 : -1;
  const pos = [];
  const nor = [];
  const uv = [];
  const tri = (a, b, c, n) => {
    const ab = [b.p[0] - a.p[0], b.p[1] - a.p[1], b.p[2] - a.p[2]];
    const ac = [c.p[0] - a.p[0], c.p[1] - a.p[1], c.p[2] - a.p[2]];
    const cx = ab[1] * ac[2] - ab[2] * ac[1];
    const cy = ab[2] * ac[0] - ab[0] * ac[2];
    const cz = ab[0] * ac[1] - ab[1] * ac[0];
    const order = cx * n[0] + cy * n[1] + cz * n[2] < 0 ? [a, c, b] : [a, b, c];
    for (const v of order) {
      pos.push(...v.p);
      nor.push(...n);
      uv.push(...v.t);
    }
  };
  for (const run of RUNS) {
    for (let i = 1; i < pts.length; i++) {
      const P = pts[i - 1];
      const Q = pts[i];
      const dd = Q.d - P.d;
      const dh = Q.h - P.h;
      const len = Math.hypot(dd, dh);
      if (len < 1e-6) continue;
      const n = run.nrm((sign * dh) / len, (-sign * dd) / len);
      const v = (pt, s) => ({ p: run.pos(pt.d, pt.h, s), t: run.uv(pt.u, s) });
      const a = v(P, P.d);
      const b = v(P, run.L - P.d);
      const c = v(Q, run.L - Q.d);
      const d = v(Q, Q.d);
      tri(a, b, c, n);
      tri(a, c, d, n);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  return g;
};

// UVs are in centimetres; the texture's repeat says how many cm one copy spans.
const useStone = (photo, onSource) => {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    let alive = true;
    const ready = (t, w, h, span, source) => {
      if (!alive) {
        t.dispose();
        return;
      }
      t.wrapS = THREE.MirroredRepeatWrapping;
      t.wrapT = THREE.MirroredRepeatWrapping;
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      // one copy spans `span` cm; centre the picture on the top so slab-photo borders stay off it
      const spanY = span * (h / w);
      t.repeat.set(1 / span, 1 / spanY);
      t.offset.set(0.5 - TOP_L / 2 / span, 0.5 + TOP_W / 2 / spanY);
      t.needsUpdate = true;
      setTex(t);
      onSource?.(source);
    };
    const drawn = () => ready(new THREE.CanvasTexture(tajMahalCanvas(1024)), 1, 1, 70, "drawn");
    if (!photo) {
      drawn();
    } else {
      new THREE.TextureLoader()
        .setCrossOrigin("anonymous")
        .load(photo, (t) => ready(t, t.image.width, t.image.height, 200, "photo"), undefined, drawn);
    }
    return () => {
      alive = false;
    };
  }, [photo, onSource]);
  useEffect(() => () => tex?.dispose(), [tex]);
  return tex;
};

const Studio = () => {
  const { gl, scene } = useThree();
  useEffect(() => {
    gl.toneMapping = THREE.NeutralToneMapping;
    gl.toneMappingExposure = 1;
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.45).texture;
    scene.environment = env;
    scene.environmentIntensity = 0.4;
    return () => {
      scene.environment = null;
      env.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);
  return null;
};

const Fit = () => {
  const { camera, size } = useThree();
  const aspect = size.width / Math.max(1, size.height);
  useEffect(() => {
    const d = 92 * Math.max(1, 1.35 / aspect);
    const polar = 1.27;
    const az = 0.38;
    camera.position.set(
      TARGET[0] + d * Math.sin(polar) * Math.sin(az),
      TARGET[1] + d * Math.cos(polar),
      TARGET[2] + d * Math.sin(polar) * Math.cos(az),
    );
    camera.lookAt(...TARGET);
    camera.updateProjectionMatrix();
  }, [camera, aspect]);
  return null;
};

// Slow turntable until the visitor grabs it, then plain orbit + zoom. With `sync`, two
// panes share one camera pose: whichever was grabbed last drives, the other follows.
const Orbit = ({ sync }) => {
  const { camera, gl } = useThree();
  const ref = useRef(null);
  useEffect(() => {
    const c = new ThreeOrbitControls(camera, gl.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.enablePan = false;
    c.autoRotate = true;
    c.autoRotateSpeed = 0.7;
    c.maxPolarAngle = Math.PI / 2 - 0.05;
    c.minDistance = 14;
    c.maxDistance = 240;
    c.target.set(...TARGET);
    c.update();
    const stop = () => {
      c.autoRotate = false;
      if (sync) {
        sync.shared.driver = sync.id;
        sync.shared.auto = false;
      }
    };
    c.addEventListener("start", stop);
    ref.current = c;
    return () => {
      c.removeEventListener("start", stop);
      c.dispose();
    };
  }, [camera, gl, sync]);
  useFrame(() => {
    const c = ref.current;
    if (!c) return;
    if (!sync) {
      c.update();
      return;
    }
    const s = sync.shared;
    if (s.driver == null) s.driver = sync.id;
    if (s.driver === sync.id) {
      c.autoRotate = s.auto !== false;
      c.update();
      camera.position.toArray(s.pos);
      c.target.toArray(s.target);
    } else {
      c.autoRotate = false;
      camera.position.fromArray(s.pos);
      c.target.fromArray(s.target);
      camera.lookAt(c.target);
    }
  });
  return null;
};

const Capture = ({ api }) => {
  const get = useThree((s) => s.get);
  useEffect(() => {
    api.current = () => {
      const { gl, scene, camera } = get();
      gl.render(scene, camera);
      return gl.domElement.toDataURL("image/png");
    };
    return () => {
      api.current = null;
    };
  }, [get, api]);
  return null;
};

const Top = ({ profile, finish, tex }) => {
  const geo = useMemo(() => buildTop(profile), [profile]);
  useEffect(() => () => geo.dispose(), [geo]);
  const mat = useMemo(() => {
    const polished = finish === "polished";
    return new THREE.MeshPhysicalMaterial({
      map: tex,
      color: tex ? "#ffffff" : "#d9d0c0",
      roughness: polished ? 0.26 : 0.6,
      clearcoat: polished ? 0.4 : 0,
      clearcoatRoughness: 0.22,
      specularIntensity: polished ? 0.55 : 0.25,
      envMapIntensity: polished ? 0.55 : 0.25,
    });
  }, [tex, finish]);
  useEffect(() => () => mat.dispose(), [mat]);
  return <mesh geometry={geo} material={mat} castShadow receiveShadow />;
};

const Cabinet = ({ laminated }) => {
  const inset = laminated ? 5 : 2.5;
  return (
    <mesh position={[TOP_L / 2, -SLAB - CAB_H / 2, -TOP_W / 2]} castShadow receiveShadow>
      <boxGeometry args={[TOP_L - inset * 2, CAB_H, TOP_W - inset * 2]} />
      <meshStandardMaterial color="#ece8e1" roughness={0.92} />
    </mesh>
  );
};

export const EdgeScene = ({ profile, finish, photo, onSource, captureApi, testId, sync }) => {
  const tex = useStone(photo, onSource);
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      gl={{ antialias: true, preserveDrawingBuffer: true }}
      camera={{ fov: 30, near: 0.5, far: 3000 }}
      data-testid={testId}
    >
      <color attach="background" args={["#f3f1ec"]} />
      <Studio />
      <Fit />
      <Orbit sync={sync} />
      {captureApi && <Capture api={captureApi} />}
      <ambientLight intensity={0.18} />
      <hemisphereLight intensity={0.25} color="#ffffff" groundColor="#d8d1c6" />
      <directionalLight
        position={[40, 120, 90]}
        intensity={0.7}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0004}
        shadow-camera-left={-90}
        shadow-camera-right={90}
        shadow-camera-top={90}
        shadow-camera-bottom={-90}
        shadow-camera-far={400}
      />
      <group position-y={-SLAB - CAB_H}>
        <mesh rotation-x={-Math.PI / 2} position-y={-0.02}>
          <planeGeometry args={[600, 600]} />
          <meshStandardMaterial color="#f4f1eb" roughness={1} />
        </mesh>
        <mesh rotation-x={-Math.PI / 2} receiveShadow>
          <planeGeometry args={[600, 600]} />
          <shadowMaterial opacity={0.22} />
        </mesh>
      </group>
      <Cabinet laminated={profile.type === "laminated"} />
      <Top profile={profile} finish={finish} tex={tex} />
    </Canvas>
  );
};
